import { useTranslation } from "react-i18next";
import type { TServiceSchema } from "../Models";
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger, UseFormWatch } from "react-hook-form";
import React, { JSX, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PermanentServiceFragment, RevenueCalculationType } from "@/graphql/__generated__/graphql";
import { FormattedNumberInputField } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface RevenueCalculationTypeComponentProps {
  control: Control<any>;
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  trigger: UseFormTrigger<any>;
  errors: FieldErrors<any>;
  disabled?: boolean;
  service?: PermanentServiceFragment;
  defaultValues?: Partial<TServiceSchema>;
  children?: JSX.Element | null;
};

function RevenueCalculationTypeComponent(props: RevenueCalculationTypeComponentProps) {
  const { control, watch, setValue, trigger, errors, disabled, service, defaultValues, children } = props;

  const { t } = useTranslation(["translation", "components"]);
  const revenueCalculationType = watch("revenueCalculationType");

  const hourlyRateAbsolutes = [
    {
      key: "hourlyRateDayAbsolute",
      label: t("permanentservice.inputs.hourlyratedayabsolute.label"),
      placeholder: t("permanentservice.inputs.hourlyratedayabsolute.placeholder")
    },
    {
      key: "hourlyRateNightAbsolute",
      label: t("permanentservice.inputs.hourlyratenightabsolute.label"),
      placeholder: t("permanentservice.inputs.hourlyratenightabsolute.placeholder")
    },
    {
      key: "hourlyRateSundayAbsolute",
      label: t("permanentservice.inputs.hourlyratesundayabsolute.label"),
      placeholder: t("permanentservice.inputs.hourlyratesundayabsolute.placeholder")
    },
    {
      key: "hourlyRateSundayNightAbsolute",
      label: t("permanentservice.inputs.hourlyratesundaynightabsolute.label"),
      placeholder: t("permanentservice.inputs.hourlyratesundaynightabsolute.placeholder")
    },
    {
      key: "hourlyRateHolidayAbsolute",
      label: t("permanentservice.inputs.hourlyrateholidayabsolute.label"),
      placeholder: t("permanentservice.inputs.hourlyrateholidayabsolute.placeholder")
    },
    {
      key: "hourlyRateHolidayNightAbsolute",
      label: t("permanentservice.inputs.hourlyrateholidaynightabsolute.label"),
      placeholder: t("permanentservice.inputs.hourlyrateholidaynightabsolute.placeholder")
    }
  ];

  const hourlyRateRelatives = [
    {
      key: "hourlyRateDayAbsolute",
      label: t("permanentservice.inputs.hourlyratedayabsoluterelative.label"),
      placeholder: t("permanentservice.inputs.hourlyratedayabsoluterelative.placeholder"),
      isCurrency: true
    },
    {
      key: "hourlyRateNightRelative",
      label: t("permanentservice.inputs.hourlyratenightrelative.label"),
      placeholder: t("permanentservice.inputs.hourlyratenightrelative.placeholder"),
      isBp: true
    },
    {
      key: "hourlyRateSundayRelative",
      label: t("permanentservice.inputs.hourlyratesundayrelative.label"),
      placeholder: t("permanentservice.inputs.hourlyratesundayrelative.placeholder"),
      isBp: true
    },
    {
      key: "hourlyRateSundayNightRelative",
      label: t("permanentservice.inputs.hourlyratesundaynightrelative.label"),
      placeholder: t("permanentservice.inputs.hourlyratesundaynightrelative.placeholder"),
      isBp: true
    },
    {
      key: "hourlyRateHolidayRelative",
      label: t("permanentservice.inputs.hourlyrateholidayrelative.label"),
      placeholder: t("permanentservice.inputs.hourlyrateholidayrelative.placeholder"),
      isBp: true
    },
    {
      key: "hourlyRateHolidayNightRelative",
      label: t("permanentservice.inputs.hourlyrateholidaynightrelative.label"),
      placeholder: t("permanentservice.inputs.hourlyrateholidaynightrelative.placeholder"),
      isBp: true
    }
  ];


  /**
   * Reset fields that is affected by revenue calculation type
   */
  useEffect(() => {
    if (revenueCalculationType === RevenueCalculationType.Margin) {
      setValue("margin", defaultValues?.margin ?? undefined);
      setValue("flatRate", undefined);
      setValue("hourlyRateAbsolute", undefined);
      setValue("hourlyRateRelative", undefined);
    } else if (revenueCalculationType === RevenueCalculationType.Flatrate) {
      setValue("flatRate", defaultValues?.flatRate ?? undefined);
      setValue("margin", undefined);
      setValue("hourlyRateAbsolute", undefined);
      setValue("hourlyRateRelative", undefined);
    } else if (revenueCalculationType === RevenueCalculationType.Hourlyrateabsolute) {
      setValue("hourlyRateAbsolute", defaultValues?.hourlyRateAbsolute ?? undefined);
      setValue("margin", undefined);
      setValue("flatRate", undefined);
      setValue("hourlyRateRelative", undefined);
    } else if (revenueCalculationType === RevenueCalculationType.Hourlyraterelative) {
      setValue("hourlyRateRelative", defaultValues?.hourlyRateRelative ?? undefined);
      setValue("margin", undefined);
      setValue("flatRate", undefined);
      setValue("hourlyRateAbsolute", undefined);
    }
  }, [revenueCalculationType]);

  return (
    <React.Fragment>
      <div className="flex-wrap-4 justify-between items-end">
        {/* Revenue Calculation Type */}
        <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2">
          <Label htmlFor="revenueCalculationType" className={cn(errors.revenueCalculationType && "text-red-500")}>
            {t("permanentservice.inputs.revenuecalculationtype.label")}
          </Label>
          <Select
            onValueChange={(value: RevenueCalculationType) => {
              setValue("revenueCalculationType", value);
              trigger("revenueCalculationType");
            }}
            value={watch("revenueCalculationType") || service?.revenueCalculationType || ""}
            disabled={disabled}
          >
            <SelectTrigger id="revenueCalculationType">
              <SelectValue placeholder={t("permanentservice.inputs.revenuecalculationtype.selectplaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {Object.values(RevenueCalculationType).map((type) => (
                <SelectItem key={type} value={String(type)}>
                  {t("common:revenuecalculationtype." + type.toLowerCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.revenueCalculationType && (
            <p className="input-error-message">
              {errors.revenueCalculationType.message as string}
            </p>
          )}
        </div>

        {/* Margin */}
        {
          revenueCalculationType === RevenueCalculationType.Margin && (
            <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2">
              <Label htmlFor="margin" className={cn(errors.margin && "text-red-500")}>
                {t("permanentservice.inputs.margin.label")}
              </Label>
              <FormattedNumberInputField
                name="margin"
                control={control}
                additionalSuffix="%"
                style="decimal"
                placeholder={t("permanentservice.inputs.margin.placeholder")}
              />
              {errors.margin && (
                <p className="input-error-message">
                  {errors.margin.message as string}
                </p>
              )}
            </div>
          )
        }

        {/* Flat Rate */}
        {
          revenueCalculationType === RevenueCalculationType.Flatrate && (
            <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2">
              <Label htmlFor="flatRate" className={cn(errors.flatRate && "text-red-500")}>
                {t("permanentservice.inputs.flatrate.label")}
              </Label>
              <FormattedNumberInputField
                name="flatRate"
                control={control}
                disabled={disabled}
                placeholder={t("permanentservice.inputs.flatrate.placeholder")}
                style="currency"
              />
              {errors.flatRate && (
                <p className="input-error-message">
                  {errors.flatRate.message as string}
                </p>
              )}
            </div>
          )
        }

        {
          children && (
            <div className="ml-auto">
              {children}
            </div>
          )
        }
      </div>


      {/* Hourly Rate Absolute */}
      {
        revenueCalculationType === RevenueCalculationType.Hourlyrateabsolute && (
          <Card className="flex-col-4 p-4">
            <p className="text-sm text-slate-400">{t("permanentservice.hourlyrateabsolutetitle")}</p>
            <div className="flex flex-wrap items-start justify-between gap-4">
              {
                hourlyRateAbsolutes.map(({ key, label, placeholder }) => (
                  <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2" key={`hourlyRateAbsolute-${key}`}>
                    <Label htmlFor={key} className={cn(errors[key] && "text-red-500")}>
                      {label}
                    </Label>
                    <FormattedNumberInputField
                      name={`hourlyRateAbsolute.${key}`}
                      control={control}
                      additionalSuffix="/h"
                      disabled={disabled}
                      placeholder={placeholder}
                      style="currency"
                    />
                    {errors.hourlyRateAbsolute?.[key] && (
                      <p className="input-error-message">
                        {errors.hourlyRateAbsolute?.[key]?.message as string}
                      </p>
                    )}
                  </div>
                ))
              }
            </div>
            {errors.hourlyRateAbsolute && (
              <p className="input-error-message">
                {errors.hourlyRateAbsolute.root?.message as string}
              </p>
            )}
          </Card>
        )
      }

      {/* Hourly Rate Relative */}
      {
        revenueCalculationType === RevenueCalculationType.Hourlyraterelative && (
          <Card className="flex-col-4 p-4">
            <p className="text-sm text-slate-400">{t("permanentservice.hourlyraterelativetitle")}</p>
            <div className="flex flex-wrap items-start justify-between gap-4">
              {
                hourlyRateRelatives.map(({ key, label, isCurrency, placeholder }) => (
                  <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2" key={`hourlyRateRelative-${key}`}>
                    <Label htmlFor={key} className={cn(errors[key] && "text-red-500")}>
                      {label}
                    </Label>
                    <FormattedNumberInputField
                      name={`hourlyRateRelative.${key}`}
                      control={control}
                      additionalSuffix={isCurrency ? "/h" : "%"}
                      disabled={disabled}
                      placeholder={placeholder}
                      style={isCurrency ? "currency" : "decimal"}
                    />
                    {errors.hourlyRateRelative?.[key] && (
                      <p className="input-error-message">
                        {errors.hourlyRateRelative?.[key]?.message as string}
                      </p>
                    )}
                  </div>
                ))
              }
            </div>
            {errors.hourlyRateRelative && (
              <p className="input-error-message">
                {errors.hourlyRateRelative.root?.message as string}
              </p>
            )}
          </Card>
        )
      }
    </React.Fragment>
  );
}

export default RevenueCalculationTypeComponent;
